import {Tabs} from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {compose} from 'redux';
import MonthConfigurationTab from './MonthConfigurationTab';
import LoadingIndicator from '../../common/loading/LoadingIndicator';
import withAuthorization from '../../../services/Authorization';
import Constants from '../../../services/Constants';
import Time from '../../../services/Time';

const TabPane = Tabs.TabPane;

class MonthConfiguration extends React.Component {

  static propTypes = {
    dispatchFetchMonthlyPlanning: PropTypes.func.isRequired,
    dispatchSaveMonthlyPlanning: PropTypes.func.isRequired,
    fetchMonthlyPlanning: PropTypes.shape({
      monthlyPlanning: PropTypes.shape({
        maintenancePlanning: PropTypes.object,
        bodyworkPlanning: PropTypes.object
      })
    }),
    fetchPlanningManagement: PropTypes.shape({
      planningManagement: PropTypes.shape({
        planning: PropTypes.object
      })
    })
  };

  constructor(props) {
    super(props);
    this.state = {
      monthlyPlanning: {
        maintenancePlanning: {},
        bodyworkPlanning: {}
      },
      planning: {},
      pristine: true,
      selectedDay: moment().format(Constants.DATE_FORMAT_API),
      selectedCalendar: 'MAINTENANCE'
    }
  }

  componentDidMount() {
    this.props.dispatchFetchMonthlyPlanning(this.state.selectedDay);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const prevPlanning = prevProps.fetchMonthlyPlanning && prevProps.fetchMonthlyPlanning.monthlyPlanning;
    const nextPlanning = this.props.fetchMonthlyPlanning && this.props.fetchMonthlyPlanning.monthlyPlanning;
    if (nextPlanning && nextPlanning !== prevPlanning) {
      this.setState(prevState => ({
        monthlyPlanning: {
          bodyworkPlanning: {
            ...nextPlanning.bodyworkPlanning,
            ...prevState.monthlyPlanning.bodyworkPlanning,
          },
          maintenancePlanning: {
            ...nextPlanning.maintenancePlanning,
            ...prevState.monthlyPlanning.maintenancePlanning,
          },
        }
      }));
    }

    const prevPlanningManagement = prevProps.fetchPlanningManagement && prevProps.fetchPlanningManagement.planningManagement;
    const nextPlanningManagement = this.props.fetchPlanningManagement && this.props.fetchPlanningManagement.planningManagement;
    if (nextPlanningManagement && nextPlanningManagement.planning && nextPlanningManagement !== prevPlanningManagement) {
      this.setState({planning: nextPlanningManagement.planning});
    }
  }

  save = () => {
    this.props.dispatchSaveMonthlyPlanning({
      bodyworkPlanning: this.filterPastDates(this.state.monthlyPlanning.bodyworkPlanning),
      maintenancePlanning: this.filterPastDates(this.state.monthlyPlanning.maintenancePlanning)
    });
    this.setState({pristine: true});
  };

  // do not save past date as they aren't editable
  filterPastDates(planning) {
    return Object.keys(planning || {}).reduce((acc, key) => {
      if (!Time.beforeToday(key)) {
        acc[key] = planning[key];
      }
      return acc;
    }, {})
  }

  updateDay = (day, value, planningType) => {
    const {monthlyPlanning} = this.state;
    const currentDayInPlanning = monthlyPlanning[planningType][day];
    // if new status is CLOSED or old status is closed, both plannings must be updated
    if ((value.dayStatus === Constants.DAY_CLOSED.value) || (currentDayInPlanning && currentDayInPlanning.dayStatus === Constants.DAY_CLOSED.value)) {
      monthlyPlanning.maintenancePlanning[day] = value;
      monthlyPlanning.bodyworkPlanning[day] = value;
    } else {
      monthlyPlanning[planningType][day] = value;
    }
    this.setState({
      monthlyPlanning,
      pristine: false
    });
  };

  updateSelectedDay = (selectedDay) => {
    this.setState({selectedDay});
  };

  updateSelectedCalendar = (selectedCalendar) => {
    this.setState({selectedCalendar});
  };

  maxPerDayOfWeek = () => {
    const {planningManagement: {planning = {}}} = this.props.fetchPlanningManagement;
    const maxPerDay = {};

    Object.keys(planning).forEach(key => {
      const slots = planning[key].slots || [];
      maxPerDay[key] = slots.map(s => s.maxTotalArrivals)
        .reduce((accumulator, total) => accumulator + total, 0);

      if (this.hasTwentyFourSeven()) {
        maxPerDay[key] += planning[key].maxTwentyFourSevenArrivals || 0;
      }
    });
    return maxPerDay;
  };

  hasTwentyFourSeven = () => {
    const {fetchMainServices: {mainServices = {}}} = this.props;
    return mainServices.offering && mainServices.offering.carDropOff;
  };

  computeTotalOngoing = (date) => {
    const {monthlyPlanning: {bodyworkPlanning, maintenancePlanning}} = this.state;
    const bodyworkDay = bodyworkPlanning[date] || {};
    const maintenanceDay = maintenancePlanning[date] || {};

    const bodyworkInterventions = (Object.keys(bodyworkDay).length > 0) ? bodyworkDay.currentInterventions : 0;
    const maintenanceInterventions = (Object.keys(maintenanceDay).length > 0) ? maintenanceDay.currentInterventions : 0;
    return bodyworkInterventions + maintenanceInterventions;
  };

  render() {
    const {intl: {formatMessage}, fetchMonthlyPlanning, configurationHints} = this.props;

    const maxPerDayOfWeek = this.maxPerDayOfWeek();
    const shouldConfigureWeek = configurationHints && configurationHints.includes('WEEKLY_PLANNING');

    return (
      <section className='box'>
        <header className='box-heading'>
          <h2 className='box-heading__title'>
            {formatMessage({id: 'section.month-planning.title'})}
          </h2>
          <div className='box-heading__button'>
            {!shouldConfigureWeek && <button onClick={this.save} disabled={this.state.pristine} type='primary' className='btn btn-sm btn-primary'>{formatMessage({id: 'action.save'})}</button>}
          </div>
        </header>
        {
          (fetchMonthlyPlanning.loading) ? (
            <LoadingIndicator/>
          ) : (
            <div className='box__body'>
              {shouldConfigureWeek ? (
                <div className='alert alert--error'>{formatMessage({id: 'configuration.week-configuration.not-available'})}</div>
              ) : (
                <Tabs defaultActiveKey={this.state.selectedCalendar} type='card' onChange={this.updateSelectedCalendar}>
                  <TabPane key={'MAINTENANCE'} tab={formatMessage({id: 'section.month-planning.maintenance.title'})}>
                    <MonthConfigurationTab data={this.state.monthlyPlanning.maintenancePlanning}
                                           planning={this.state.planning}
                                           maxByWeekDay={maxPerDayOfWeek}
                                           computeAll={this.computeTotalOngoing}
                                           updateDay={(day, value) => this.updateDay(day, value, 'maintenancePlanning')}
                                           selectedDay={this.state.selectedDay}
                                           updateSelectedDay={this.updateSelectedDay}
                    />
                  </TabPane>
                  <TabPane key={'BODYWORK'} tab={formatMessage({id: 'section.month-planning.bodywork.title'})}>
                    <MonthConfigurationTab data={this.state.monthlyPlanning.bodyworkPlanning}
                                           planning={this.state.planning}
                                           maxByWeekDay={maxPerDayOfWeek}
                                           computeAll={this.computeTotalOngoing}
                                           updateDay={(day, value) => this.updateDay(day, value, 'bodyworkPlanning')}
                                           selectedDay={this.state.selectedDay}
                                           updateSelectedDay={this.updateSelectedDay}
                    />
                  </TabPane>
                </Tabs>
              )}
            </div>
          )
        }
      </section>
    )
  }
}

const mapStateToProps = state => ({
  fetchMonthlyPlanning: state.fetchMonthlyPlanning,
  saveMonthlyPlanning: state.saveMonthlyPlanning,
  fetchPlanningManagement: state.fetchPlanningManagement,
  savePlanningManagement: state.savePlanningManagement,
  fetchMainServices: state.fetchMainServices,
  configurationHints: state.dealerConfigurationCheck && state.dealerConfigurationCheck.configurationHints,
});

const mapDispatchToProps = dispatch => ({
  dispatchFetchMonthlyPlanning: (date) => dispatch({type: 'FETCH_MONTHLY_PLANNING.REQUEST', date}),
  dispatchSaveMonthlyPlanning: (body) => dispatch({type: 'SAVE_MONTHLY_PLANNING.REQUEST', body})
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  (Component) => withAuthorization(Component, ['DUI_ADMIN', 'DUI_SERVICE_ADVISOR'])
)(MonthConfiguration);
